import type { Channel } from '@/shared/api/websocket'
import type { Message } from '@/shared/api/websocket/types'

import * as React from 'react'

import { Socket } from '@/shared/api/websocket'
import { SOCKET_EVENTS } from '@/shared/api/websocket/constants'
import { LinearProgress } from '@/shared/components/ui/linear-progress'
import useCreation from '@/shared/hooks/use-creation'
import logger from '@/shared/tools/logger'

export function useSocketChannel(topic: string) {
	const { socket } = React.use(SocketContext)
	const [channel, setChannel] = React.useState<Channel | undefined>(() =>
		socket.channels.find((channel) => channel.topic === topic)
	)
	React.useEffect(() => {
		socket.on('channels', (channels: Channel[]) => {
			setChannel(channels.find((channel) => channel.topic === topic))
		})
	}, [topic])

	return channel
}
export const SocketContext = React.createContext<{
	socket: Socket
	notifications: SocketMessagesNotification<any> | null
}>({ socket: {} as Socket, notifications: null })

enum ConnectionStatus {
	CONNECTED = 'connected',
	DISCONNECTED = 'disconnected',
}
export type SocketMessagesNotification<T> = {
	subscribe: () => void
	unsubscribe: () => void
	addMessage: (message: T) => void
}
export default function SocketProvider<T>({
	children,
	onConnect,
	notifications,
}: {
	children: React.ReactNode
	notifications: SocketMessagesNotification<T>
	onConnect: (socket: Socket) => void
}) {
	const socket = useCreation(
		() =>
			new Socket(`${location.host}/api/v1`, {
				params: { env: 'dev' },
				transport: WebSocket,
				encode: (data: object, cb: (v: string) => void) =>
					cb(JSON.stringify(data)),
				decode: (data: string, cb: (v: object) => void) => cb(JSON.parse(data)),
				logger: (kind: string, msg: unknown, data: unknown) => {
					logger.debug('CONN', `${kind}: ${msg}`, data as any)
				},
			}),
		[]
	)
	const [connectionStatus, setConnectionStatus] =
		React.useState<ConnectionStatus | null>(null)

	const handleAuthenticate = React.useEffectEvent(() => onConnect(socket))

	React.useEffect(() => {
		logger.debug('WS', 'Create new Socket')
		socket.connect()
		socket.on(SOCKET_EVENTS.open, () =>
			setConnectionStatus(ConnectionStatus.CONNECTED)
		)
		socket.on(SOCKET_EVENTS.close, () =>
			setConnectionStatus(ConnectionStatus.DISCONNECTED)
		)
		socket.on(SOCKET_EVENTS.error, () =>
			setConnectionStatus(ConnectionStatus.DISCONNECTED)
		)
		socket.on(SOCKET_EVENTS.message, (message: Message) => {
			logger.debug('WS', 'new message', message)
		})

		// Authenticate socket
		socket.auth.authenticate(handleAuthenticate)

		return () => {
			socket.disconnect()
		}
	}, [])

	return (
		<SocketContext
			value={React.useMemo(() => ({ socket, notifications }), [notifications])}
		>
			{connectionStatus !== ConnectionStatus.CONNECTED && (
				<LinearProgress className='fixed top-0 right-0 left-0 z-50' />
			)}
			{children}
		</SocketContext>
	)
}
